import React, { useContext, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import logo from '../../images/logo.png';
import { AuthContext } from '../AuthContext';
import LoadingComponent from '../../utils/LoadingComponent';
import { showSwalNotification } from '../../utils/SwalResponse';
import MyWebsite from '../../utils/MyWebsite';

const forestGreen = '#228B22';

const OTPVerification = () => {
  const { userId } = useParams();
  const { adminLoginVerification, resendOTP, loading } = useContext(AuthContext);
  const [otp, setOtp] = useState('');
  const [website, setWebsite] = useState('');
  const [message, setMessage] = useState('');
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const report = queryParams.get('rep');
  const eventId = queryParams.get('event_id');
  const issueId = queryParams.get('issue_id');

  // Handle OTP verification
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (website) {
      setMessage('Bot-like behavior detected.');
      return;
    }

    if (!!userId && !!otp && otp.length === 6) {
      const response = await adminLoginVerification({ otp: otp, user_id: userId });
      setMessage(response?.message);

      if (response?.status === 'success') {
        showSwalNotification(response);

        if (!!report) {
          if (report === 'submit') {
            navigate('/report');
            return;
          } else if (report === 'reg_event' && eventId) {
            await navigate(`/events/${eventId}`);
            return;
          } else if (report === 'attend' && eventId) {
            await navigate(`/events/${eventId}/attendance`);
            return;
          } else if (report === 'review' && issueId) {
            await navigate(`/reviews/add/${issueId}`);
            return;
          }
        }

        navigate('/dashboard');
      }
    }
  };

  // Handle OTP resend
  const handleResend = async () => {
    if (!userId) return;
    try {
      setResending(true);
      setMessage('');
      const response = await resendOTP({ user_id: userId });
      showSwalNotification(response);
      setMessage(response?.message || 'A new OTP has been sent.');

      // Start 30s cooldown timer
      setResendCooldown(30);
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Resend OTP error:', error);
      setMessage('Failed to resend OTP. Please try again.');
    } finally {
      setResending(false);
    }
  };

  if (!!loading) {
    return <LoadingComponent loadingText={'Verifying ...'} />;
  }

  return (
    <motion.div
      className="d-flex justify-content-center align-items-center vh-100"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        className="card p-4 m-3 shadow-lg"
        style={{
          width: '100%',
          maxWidth: '480px',
          borderRadius: '16px',
          border: `1px solid ${forestGreen}`,
          backgroundColor: 'rgba(255,255,255,0.5)',
        }}
        initial={{ y: 30, scale: 0.98 }}
        animate={{ y: 0, scale: 1 }}
        transition={{ duration: 0.4 }}
      >
        {/* Logo */}
        <div className="text-center mb-3">
          <img src={logo} alt="App Logo" style={{ width: '150px', height: 'auto' }} />
        </div>

        <h3
          className="text-center mb-3"
          style={{ color: forestGreen, fontWeight: 600, fontSize: '1.7rem' }}
        >
          OTP Verification
        </h3>

        <p className="text-center text-muted mb-4" style={{ fontSize: '0.95rem' }}>
          An OTP has been sent to your email. It will expire in <strong>5 minutes</strong>.
        </p>

        {message && (
          <div className="alert alert-success text-center">{message}</div>
        )}

        <form onSubmit={handleSubmit}>
          <MyWebsite onChange={(e) => setWebsite(e.target.value)} />

          {/* OTP Input */}
          <motion.div className="form-group mb-4" whileFocus={{ scale: 1.02 }}>
            <label className="mb-1" style={{ color: '#333', fontWeight: 500 }}>
              🔐 Enter OTP
            </label>
            <input
              type="text"
              className="form-control text-center"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
              maxLength={6}
              placeholder="Enter 6-digit code"
              style={{ borderColor: forestGreen, fontWeight: 600 }}
            />
          </motion.div>

          <motion.button
            type="submit"
            className="btn w-100"
            style={{
              backgroundColor: forestGreen,
              color: '#fff',
              fontWeight: 600,
              letterSpacing: 0.5,
              borderRadius: '8px',
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Verify OTP
          </motion.button>
        </form>

        {/* Resend OTP */}
        <div className="text-center mt-3">
          <motion.button
            onClick={handleResend}
            className="btn btn-link"
            style={{
              color: forestGreen,
              fontWeight: 500,
              textDecoration: 'none',
              cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
            }}
            whileHover={{ scale: resendCooldown > 0 ? 1 : 1.05 }}
            disabled={resendCooldown > 0 || resending}
          >
            {resending
              ? 'Resending...'
              : resendCooldown > 0
              ? `Resend available in ${resendCooldown}s`
              : 'Resend OTP'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default OTPVerification;
